/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSubheader } from "../../../../_metronic/layout";
import { TextBox } from "../../components/pageparts/TextBox";
import {
  fetchProductionProcessBaselines,
  deleteBaseline,
  deleteInterventionDataset,
} from "../../apicalls/farmFunctions";
import { organiseBaselines } from "../../../modules/Helpers/organise-baselines";
import DeleteConfirmationDialog from "../../../sustell_15/components/helpers/DeleteConfirmationDialog";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import {
  FormType,
  DatasetType,
  isPartnerIngestedFarm,
} from "../../components/FarmFlow/common";
import BaselineSection from "../../components/FarmFlow/BaselineSection";
import InterventionSection from "../../components/FarmFlow/InterventionSection";
import ArchiveSection from "../../components/FarmFlow/ArchiveSection";
import useAdobeDataLayer from "../../analytics/adobeDataLayer";

export const SustellProdProcessDatasetsPageV2 = (props) => {
  const intl = useIntl();
  const history = useHistory();
  const subheader = useSubheader();
  const { customerID, farmID, processID } = props.match.params;
  const farmName = props?.location?.state?.farmName || "Farm";

  const [prodProcess, setProdProcess] = useState({});
  const [activeBaseline, setActiveBaseline] = useState(null);
  const [archivedBaselines, setArchivedBaselines] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [farmCountry, setFarmCountry] = useState("");
  const [selectedFarmName, setSelectedFarmName] = useState(farmName);
  const [benchmark, setBenchmark] = useState(false);
  // True when the farm's data was ingested via the partner API (read-only in UI).
  const [partnerReadOnly, setPartnerReadOnly] = useState(false);

  const { ctaClickEvent } = useAdobeDataLayer();

  // --- Data ---
  const setProductionProcessData = (fetchedData) => {
    // fetch successeded only one matching PP has been retrieved
    const selProdProcess = fetchedData.animalSystems?.items[0] || {};

    if (selProdProcess && selProdProcess.baselines) {
      const extractedBaselines = [...selProdProcess.baselines.items];
      selProdProcess.baselines = extractedBaselines;
    }

    const [activeBaseline, archivedBaselines] =
      organiseBaselines(selProdProcess);

    setActiveBaseline(activeBaseline);
    setArchivedBaselines(archivedBaselines);
    setProdProcess(selProdProcess);
    setFarmCountry(fetchedData.country);
    setBenchmark(fetchedData.benchmark);
    setPartnerReadOnly(isPartnerIngestedFarm(fetchedData.lastUpdatedBy));
    setSelectedFarmName(fetchedData.name);

    subheader.setBreadcrumbs([
      {
        pathname: `/customers/${customerID}/myfarms`,
        title: intl.formatMessage({ id: "FARMS.CARD.TITLE" }),
        state: {
          animalType: selProdProcess?.animalType,
        },
      },
      {
        pathname: `/customers/${customerID}/farm/${farmID}`,
        title: selectedFarmName,
        state: {
          animalType: selProdProcess?.animalType,
        },
      },
      {
        pathname: `/customers/${customerID}/farm/${farmID}/pp/${processID}`,
        title: selProdProcess.name,
        state: {
          animalType: selProdProcess?.animalType,
        },
      },
    ]);
  };

  const fetchProductionDatasets = async (prodProcId) => {
    fetchProductionProcessBaselines(
      farmID,
      processID,
      setProductionProcessData,
      (error) => console.log(error),
    );
  };

  useEffect(() => {
    fetchProductionDatasets(processID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processID]);

  // --- Analytics ---
  const handleCtaClickEvent = (ctaType, ctaActionLabel) =>
    ctaClickEvent(
      window.location.href,
      ctaType,
      ctaActionLabel,
      "Baseline interventions",
      "Production processes",
    );

  // --- Navigation ---
  const openDataset = (
    datasetType,
    baselineId,
    formType,
    interventionReference,
  ) => {
    if (formType === FormType.Add) {
      handleCtaClickEvent("button", `Create ${datasetType}`);
    }
    if (formType === FormType.View) {
      handleCtaClickEvent("link", `View ${datasetType}`);
    }
    if (formType === FormType.Edit) {
      handleCtaClickEvent("link", `Edit ${datasetType}`);
    }

    if (datasetType === DatasetType.BASELINE) {
      history.push(
        `/customers/${customerID}/farm/${farmID}/pp/${processID}/nds/${baselineId}`,
        {
          farmName: selectedFarmName,
          processName: prodProcess?.name || "Process",
          animalType: prodProcess?.animalType,
          formType,
        },
      );
      return;
    }

    history.push(
      `/customers/${customerID}/farm/${farmID}/pp/${processID}/nint/${
        interventionReference || "NEW"
      }`,
      {
        farmName: selectedFarmName,
        processName: prodProcess?.name || "Process",
        animalType: prodProcess?.animalType,
        datasetID: baselineId,
        farmCountry, // Needed for 3NOP
        formType,
      },
    );
  };

  const handleDuplicate = (datasetType, item) => {
    const proceed = () => {
      handleCtaClickEvent("link", `Duplicate ${datasetType}`);

      if (datasetType === DatasetType.BASELINE) {
        history.push(
          `/customers/${customerID}/farm/${farmID}/pp/${processID}/nds/NEW`,
          {
            farmName: selectedFarmName,
            processName: prodProcess?.name || "Process",
            animalType: prodProcess?.animalType,
            formType: FormType.Add,
            duplicateDatasetID: item.id,
          },
        );
        return;
      }

      history.push(
        `/customers/${customerID}/farm/${farmID}/pp/${processID}/nint/NEW`,
        {
          farmName: selectedFarmName,
          processName: prodProcess?.name || "Process",
          animalType: prodProcess?.animalType,
          duplicateInterventionRef: item.reference,
          datasetID: item.baseline,
          farmCountry, // Needed for 3NOP
        },
      );
    };

    proceed();
  };

  // --- Delete ---
  const getBaselineInterventions = (baselineName) => {
    const baseline = prodProcess?.baselines?.find(
      (item) => item.name === baselineName,
    );
    if (baseline)
      return prodProcess?.interventions?.items
        ?.filter((item) => item.baseline === baseline.reference)
        ?.map((item) => item.name);
    return null;
  };

  const makeDeleteHandler = (type) => (itemName) => {
    setItemToDelete({
      itemName,
      type,
      itemType:
        type === DatasetType.BASELINE
          ? intl.formatMessage({ id: "SUSTELL.PRODUCTION_PROCESS.BASELINE" })
          : intl.formatMessage({
              id: "SUSTELL.PRODUCTION_PROCESS.INTERVENTION",
            }),
      dependencies:
        type === DatasetType.BASELINE
          ? getBaselineInterventions(itemName)
          : null,
    });
  };

  const onDeleteDatasetSuccess = (result) => {
    fetchProductionDatasets(processID);
    setItemToDelete(null);
  };

  const onDeleteDatasetError = (error) => {
    console.log(error);
  };

  const handleDelete = (datasetType, datasetName) => {
    handleCtaClickEvent("link", `Delete ${datasetType}`);

    if (datasetType === DatasetType.BASELINE) {
      deleteBaseline(
        farmID,
        datasetName,
        onDeleteDatasetSuccess,
        onDeleteDatasetError,
      );
    }

    if (datasetType === DatasetType.INTERVENTION) {
      deleteInterventionDataset(
        customerID,
        farmID,
        processID,
        datasetName,
        onDeleteDatasetSuccess,
        onDeleteDatasetError,
      );
    }
  };

  return (
    <>
      <DeleteConfirmationDialog
        open={!!itemToDelete} // open attribute strictly expects boolean, so null was giving a warning in runtime
        confirmHandler={() => {
          handleDelete(itemToDelete?.type, itemToDelete?.itemName);
        }}
        cancelHandler={() => setItemToDelete(null)}
        itemName={itemToDelete?.itemName}
        itemType={itemToDelete?.itemType}
        dependencies={itemToDelete?.dependencies}
      />

      <TextBox
        title={prodProcess?.name}
        content={intl.formatMessage(
          { id: "SUSTELL.PROCESS.DATASETS.PAGE.DESCRIPTION" },
          { br: <br /> },
        )}
      />

      <BaselineSection
        prodProcess={prodProcess}
        activeBaseline={activeBaseline}
        benchmark={benchmark}
        partnerReadOnly={partnerReadOnly}
        customerID={customerID}
        farmID={farmID}
        processID={processID}
        selectedFarmName={selectedFarmName}
        openDataset={openDataset}
        makeDeleteHandler={makeDeleteHandler}
        handleDuplicate={handleDuplicate}
      />

      <InterventionSection
        prodProcess={prodProcess}
        activeBaseline={activeBaseline}
        benchmark={benchmark}
        partnerReadOnly={partnerReadOnly}
        customerID={customerID}
        farmID={farmID}
        processID={processID}
        selectedFarmName={selectedFarmName}
        openDataset={openDataset}
        makeDeleteHandler={makeDeleteHandler}
        handleDuplicate={handleDuplicate}
      />

      <ArchiveSection
        prodProcess={prodProcess}
        archivedBaselines={archivedBaselines}
        partnerReadOnly={partnerReadOnly}
        customerID={customerID}
        farmID={farmID}
        processID={processID}
        selectedFarmName={selectedFarmName}
        openDataset={openDataset}
        makeDeleteHandler={makeDeleteHandler}
        handleDuplicate={handleDuplicate}
      />
    </>
  );
};

export default SustellProdProcessDatasetsPageV2;
